// Offers Croatian students and teachers a move to the new platform.
// Mounted once app-wide (like GroupWorkCheckInGate). It keeps showing on every page load
// until the user picks one of the two buttons. That choice is saved in localStorage per
// user and per day, so the prompt comes back the next day.

import React, { useContext, useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { AuthContext, Role } from '../context/AuthProvider';
import getNewLabUrl from '../helpers/getNewLabUrl';

const STORAGE_PREFIX = 'newPlatformPromptConfirmed_';

type StoredConfirmation = { date: string; expiresAt: number };

// Local calendar day, e.g. "2026-10-15".
const today = () => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

// TTL: the saved confirmation expires at the next local midnight.
const nextMidnight = () => {
  const date = new Date();
  date.setHours(48, 0, 0, 0);
  return date.getTime();
};

const readConfirmation = (key: string): StoredConfirmation | null => {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
};

// School computers are shared, so other users' keys pile up. Drop the expired ones.
const removeExpiredConfirmations = () => {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (!key?.startsWith(STORAGE_PREFIX)) continue;
    const stored = readConfirmation(key);
    if (!stored || !(stored.expiresAt > Date.now())) localStorage.removeItem(key);
  }
};

const isConfirmedToday = (key: string) => {
  const stored = readConfirmation(key);
  return !!stored && stored.date === today() && stored.expiresAt > Date.now();
};

export function NewPlatformPromptGate() {
  const user = useContext(AuthContext);
  const { t } = useTranslation();
  const [isDismissed, setIsDismissed] = useState(false);
  // null until localStorage has been checked.
  const [isConfirmed, setIsConfirmed] = useState<boolean | null>(null);
  console.log('hello from new platform prompt gate');

  const isCroatianStudentOrTeacher =
    !!user?.is_authenticated &&
    user.namespaces.includes('hr') &&
    (user.roles.includes(Role.student) || user.roles.includes(Role.teacher));

  // Keyed per user, since school computers are shared between students.
  const storageKey = user ? `${STORAGE_PREFIX}${user.pk}` : '';

  useEffect(() => {
    if (!isCroatianStudentOrTeacher) return;
    try {
      removeExpiredConfirmations();
      setIsConfirmed(isConfirmedToday(storageKey));
    } catch {
      // localStorage can be unavailable (e.g. blocked storage); then don't show the prompt at all.
      setIsConfirmed(true);
    }
  }, [isCroatianStudentOrTeacher, storageKey]);

  if (!isCroatianStudentOrTeacher || isConfirmed !== false || isDismissed) return null;

  // X / Esc / overlay click only hide it for this page load; it comes back on the next one.
  const close = () => setIsDismissed(true);

  // Either button counts as the user's answer for today.
  const confirm = () => {
    try {
      const stored: StoredConfirmation = { date: today(), expiresAt: nextMidnight() };
      localStorage.setItem(storageKey, JSON.stringify(stored));
    } catch {
      // Best effort; without storage the prompt just shows again next time.
    }
    setIsDismissed(true);
  };

  return (
    <Modal isOpen onClose={close} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="2xl" width="90%" maxW="500px" margin={5}>
        <ModalHeader pt={6} pb={3}>
          <Text fontSize="xl" fontWeight="bold" color="#2d3748">
            {t('newPlatformPrompt.title')}
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={3}>
          <Text color="gray.600">{t('newPlatformPrompt.description')}</Text>
        </ModalBody>

        <ModalFooter pt={3} pb={6} gap={3}>
          <Button variant="outline" onClick={confirm} borderRadius="xl" flex={1}>
            {t('newPlatformPrompt.stay')}
          </Button>
          <Button as="a" href={getNewLabUrl()} onClick={confirm} colorScheme="blue" borderRadius="xl" flex={1}>
            {t('newPlatformPrompt.goToNewPlatform')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
